import { DateTime } from 'luxon';
import type {
  FactoryBuild,
  FactoryFarmWorker,
  FactoryRelease,
  FactorySubmission,
  FactoryTask,
} from '@/lib/types/AppFactory';

export interface CurrentLiveInfo {
  submission: FactorySubmission;
  build: FactoryBuild | null;
  release: FactoryRelease | null;
}

export interface FarmLiveness {
  label: string;
  stale: boolean;
}

export class AppFactoryUtils {
  /** Factory timestamps are RFC3339 strings (unlike the epoch-seconds rest of the API). */
  static formatWhen(iso: string | undefined): string {
    if (!iso) return '—';
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_SHORT) : '—';
  }

  static sortByCreatedDesc<T extends { created: string }>(rows: T[]): T[] {
    return [...rows].sort((a, b) => (a.created < b.created ? 1 : a.created > b.created ? -1 : 0));
  }

  /** The store-live submission joined to its build and shell release, if any. */
  static currentLiveInfo(
    submissions: FactorySubmission[],
    builds: FactoryBuild[],
    releases: FactoryRelease[],
  ): CurrentLiveInfo | null {
    const live = AppFactoryUtils.sortByCreatedDesc(
      submissions.filter((s) => s.state === 'live'),
    )[0];
    if (!live) return null;
    const build = builds.find((b) => b.appBuild2Id === live.appBuild2Id) ?? null;
    const release = build
      ? releases.find((r) => r.shellReleaseId === build.shellReleaseId) ?? null
      : null;
    return { submission: live, build, release };
  }

  /** Tasks for one app, newest first. The tasks feed is account-wide. */
  static tasksForApp(tasks: FactoryTask[], appCfgId: number): FactoryTask[] {
    return AppFactoryUtils.sortByCreatedDesc(tasks.filter((t) => t.appCfgId === appCfgId));
  }

  // ── One platform's slice of an app's factory rows ──
  //
  // Builds carry their platform and submissions their store; a platform's
  // Status tab shows its own. Releases list the platforms they ship to.

  static buildsForPlatform(builds: FactoryBuild[], platform: string): FactoryBuild[] {
    return builds.filter((b) => b.platform === platform);
  }

  static submissionsForStore(submissions: FactorySubmission[], store: string): FactorySubmission[] {
    return submissions.filter((s) => s.store === store);
  }

  /** A release without a platform list (older daemons) stays visible everywhere. */
  static releasesForPlatform(releases: FactoryRelease[], platform: string): FactoryRelease[] {
    return releases.filter((r) => !r.platforms?.length || r.platforms.includes(platform));
  }

  /**
   * An app's tasks that belong to one platform. A task names only its build or
   * submission id, so it is dropped only when that id is KNOWN to be another
   * platform's — a task the lists cannot place stays visible rather than lost.
   */
  static tasksForPlatform(
    appTasks: FactoryTask[],
    builds: FactoryBuild[],
    submissions: FactorySubmission[],
    platform: string,
    store: string,
  ): FactoryTask[] {
    const buildPlatform = new Map(builds.map((b) => [b.appBuild2Id, b.platform]));
    const submissionStore = new Map(submissions.map((s) => [s.appSubmissionId, s.store]));
    return appTasks.filter((t) => {
      if (t.kind === 'build') {
        const p = buildPlatform.get(t.id);
        return p === undefined || p === platform;
      }
      if (t.kind === 'submission') {
        const s = submissionStore.get(t.id);
        return s === undefined || s === store;
      }
      return true;
    });
  }

  /**
   * A worker heartbeats every ~15s; past this window its silence means the
   * farm host is asleep/offline (builds queue honestly until it returns).
   */
  static readonly FARM_STALE_AFTER_MS = 3 * 60_000;

  /**
   * The Status tab "Farm" chip datum. Pure: `nowMs` is injected so tests
   * stay deterministic. Returns null (chip absent) when the payload has no
   * farm field — older daemons, no worker registered yet — or the
   * timestamp doesn't parse.
   */
  static farmLiveness(farm: FactoryFarmWorker | undefined, nowMs: number): FarmLiveness | null {
    if (!farm) return null;
    const seen = DateTime.fromISO(farm.seenAt);
    if (!seen.isValid) return null;
    const ageMs = nowMs - seen.toMillis();
    if (ageMs < AppFactoryUtils.FARM_STALE_AFTER_MS) {
      return { label: 'Farm: online', stale: false };
    }
    return { label: `Farm: last seen ${AppFactoryUtils.formatAge(ageMs)}`, stale: true };
  }

  /** Compact "Xm/Xh/Xd ago" for the farm chip; deterministic, no locale. */
  private static formatAge(ageMs: number): string {
    const minutes = Math.floor(ageMs / 60_000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }
}
